import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@/components/DesignSystem/Icons';
import { useCalculatorContext } from '../contexts';
import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import { FieldSelected } from '@/types/Tagular';

interface DropdownOption {
  id: number | string;
  name: string;
  slug: string;
  [key: string]: any;
}

interface CalculatorDropdownProps {
  fieldKey: string;
  label: string;
  placeholder: string;
  options: DropdownOption[];
  className?: string;
  isEditable?: boolean;
  dependsOn?: string[];
  trackingEnabled?: boolean;
  viewCorrelationId?: string;
}

const CalculatorDropdown: React.FC<CalculatorDropdownProps> = ({
  fieldKey,
  label,
  placeholder,
  options,
  className = '',
  isEditable = false,
  dependsOn = [],
  trackingEnabled = true,
  viewCorrelationId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { state, updateCalculatorValue, updateCalculatorValues, config } =
    useCalculatorContext();

  // Get current value from calculator state
  const selectedValue = state.calculatorValues[fieldKey] || '';
  const selectedOption = options.find(
    (option) => option.slug === selectedValue,
  );

  // Filter options based on search term
  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Check if field should be disabled based on dependencies
  const isDisabled =
    !isEditable ||
    dependsOn.some((dependency) => !state.calculatorValues[dependency]);

  // Track field selection with Tagular
  const trackFieldSelection = (value: string, displayValue: string) => {
    if (!trackingEnabled) return;

    const correlationId = viewCorrelationId || uuid();
    const fieldPayload: FieldSelected = {
      correlationId,
      formContext: {
        formId: config?.id || '',
        formName: config?.tracking?.formName || '',
        formType: config?.tracking?.formType || '',
        formVersion: '1.0',
      },
      userInputField: {
        fieldName: fieldKey,
        fieldLabel: label,
        fieldType: 'select',
        fieldValue: displayValue,
      },
    };
    tagularEvent('FieldSelected', fieldPayload);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleOptionClick = (option: DropdownOption) => {
    if (!isDisabled) {
      // Clear dependent fields when this field changes
      if (fieldKey === 'degree') {
        updateCalculatorValues({
          [fieldKey]: option.slug,
          category: '',
          subject: '',
        });
      } else if (fieldKey === 'category') {
        updateCalculatorValues({
          [fieldKey]: option.slug,
          subject: '',
        });
      } else {
        updateCalculatorValue(fieldKey, option.slug);
      }

      // Track the selection
      trackFieldSelection(option.slug, option.name);

      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleCardClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent dropdown from closing when typing
    e.stopPropagation();

    // Handle Enter key to select first option
    if (e.key === 'Enter' && filteredOptions.length > 0) {
      handleOptionClick(filteredOptions[0]);
    }

    // Handle Escape key to close dropdown
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const selectedDisplay = selectedOption ? selectedOption.name : placeholder;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {isEditable && (
        <div className='font-bold text-sm text-gray-900 pb-1'>{label}</div>
      )}
      <div
        className={`border border-gray-200 rounded-lg transition-all duration-200 ${
          !isEditable || isDisabled
            ? 'bg-gray-25 cursor-default px-3 py-2'
            : 'bg-white cursor-pointer hover:shadow-sm h-11 flex px-4 py-2 items-center'
        }`}
        onClick={handleCardClick}
      >
        {!isEditable && (
          <div className='font-bold text-gray-900 text-sm overflow-hidden whitespace-nowrap text-ellipsis'>
            {label}
          </div>
        )}

        <div className='flex items-center justify-between w-full'>
          <div
            className={`text-sm overflow-hidden whitespace-nowrap text-ellipsis flex-1 mr-2 ${
              selectedOption ? 'text-gray-700 font-medium' : 'text-gray-500'
            }`}
          >
            {selectedDisplay}
          </div>
          {isEditable && !isDisabled && (
            <ChevronDownIcon
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          )}
        </div>
      </div>

      {isEditable && !isDisabled && isOpen && (
        <div className='absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-60 overflow-hidden'>
          {/* Search Input */}
          <div className='p-3 border-b border-gray-200'>
            <input
              ref={searchInputRef}
              type='text'
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className='w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'
            />
          </div>

          {/* Options List */}
          <div className='max-h-48 overflow-y-auto'>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.id || index}
                  className={`px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 border-b border-gray-100 last:border-b-0 transition-colors duration-150 ${
                    selectedOption && selectedOption.slug === option.slug
                      ? 'bg-blue-50 text-blue-700'
                      : ''
                  }`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.name}
                </div>
              ))
            ) : (
              <div className='px-4 py-3 text-sm text-gray-500 italic'>
                No {label.toLowerCase()} found matching `{searchTerm}`
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculatorDropdown;
